import cx from 'classnames';
import { WIKI_PLACE_IMAGE_IN_ARTICLE_WITH_FOLLOWING_CODE } from 'mk/autogenerated/translations/PhotoPreview.6e5fb11cf81a967240011a0d81d56c45'
import { photoUrl } from 'mk/photo/photoUrl';
import Btn, { BtnLayout } from 'mk2/components/Btn';
import CroppedPreview from 'mk2/components/CroppedPreview';
import Img from 'mk2/components/Img';
import ImgCropped, { ImgCropMode } from 'mk2/components/ImgCropped';
import {
    FailedPhotoUpload,
    PendingPhotoUpload,
    PhotoUploadStatus,
    SuccessfulPhotoUpload,
} from 'mk2/helpers/form.reducers';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { AppState } from 'mk2/reducers';
import { PhotoEntity, PhotoOrientation, PhotoPreviewEntity, PhotoPreviewSchema, PhotoSchema } from 'mk2/schemas';
import { getDenormalizedEntity } from 'mk2/selectors';
import React from 'react';
import { connect } from 'react-redux';
import styles from './PhotoPreview.mscss';

interface OwnProps {
    className?: string;
    upload: PendingPhotoUpload | SuccessfulPhotoUpload | FailedPhotoUpload;
    previewPhotoSize?: string;
    showPhotoCode: boolean;
    onCancel?(uploadId: number);
    onRotate?(uploadId: number, orientation: PhotoOrientation);
}

interface OwnState {
    photoLoaded: boolean;
}

interface StateProps {
    uploadedPhoto: PhotoEntity | null;  // not null if upload.status === UploadStatus.SUCCESS
    uploadedPhotoPreview: PhotoPreviewEntity | null;  // not null if upload.status === UploadStatus.PENDING
}

interface DispatchProps {
}

type Props = OwnProps & StateProps & DispatchProps;

class PhotoPreview extends React.Component<Props, OwnState> {

    constructor(props: Props) {
        super(props);
        this.state = {photoLoaded: false};
    }

    public render() {
        const { className, upload, uploadedPhoto, uploadedPhotoPreview, previewPhotoSize, showPhotoCode, onCancel, onRotate } = this.props;
        const photoLoadedAndUploadedSuccesfullyAnd = this.state.photoLoaded && upload.status === PhotoUploadStatus.SUCCESS;
        const photoVersion = previewPhotoSize || 's150x150';

        return (
            <div
                className={cx(
                    styles.PhotoPreview,
                    (upload.status === PhotoUploadStatus.FAILURE && styles['PhotoPreview--failure']),
                    className,
                )}
            >
                    {(uploadedPhotoPreview && !photoLoadedAndUploadedSuccesfullyAnd) &&
                    <CroppedPreview
                        className={cx(styles.PhotoPreview__photo)}
                        uploadProgress={upload.status === PhotoUploadStatus.PENDING ? upload.progress : undefined}
                        imgBase64Data={uploadedPhotoPreview.imgBase64Data}
                        originalWidth={uploadedPhotoPreview.originalWidth}
                        originalHeight={uploadedPhotoPreview.originalHeight}
                        height={90}
                        width={90}
                        key="preview"
                    />
                }
                {upload.status === PhotoUploadStatus.FAILURE && (
                    <div key="name" className={styles.PhotoPreview__name}>
                        {upload.fileName}
                    </div>
                )}
                {upload.status === PhotoUploadStatus.SUCCESS && (
                    <div className={cx(styles.PhotoPreview__hiddenDivToCacheUploadedImg)}>
                        <Img
                            src={photoUrl(uploadedPhoto, photoVersion)}
                            width={uploadedPhoto.width}
                            height={uploadedPhoto.height}
                            onLoad={this.handleHiddenImageLoaded}
                            onError={this.handleHiddenImageFailed}
                            lazy={false}
                        />
                    </div>
                )}
                {photoLoadedAndUploadedSuccesfullyAnd && (
                    <ImgCropped
                        className={cx(
                            styles.PhotoPreview__photo,
                            styles[[
                                'PhotoPreview__photo--rotated0',
                                'PhotoPreview__photo--rotated90',
                                'PhotoPreview__photo--rotated180',
                                'PhotoPreview__photo--rotated270',
                            ][(upload as SuccessfulPhotoUpload).orientation]],
                        )}
                        photo={uploadedPhoto}
                        photoVersion={photoVersion}
                        height={90}
                        width={90}
                        mode={ImgCropMode.Cover}
                        lazy={false}
                    />
                )}
                {onCancel && (
                    <Btn
                        className={cx(styles.PhotoPreview__btn, styles['PhotoPreview__btn--topRight'])}
                        layout={BtnLayout.Icon}
                        icon="action-fa-times"
                        onClick={this.handleCancel}
                    />
                )}
                {onRotate && photoLoadedAndUploadedSuccesfullyAnd && (
                    <Btn
                        className={cx(styles.PhotoPreview__btn, styles['PhotoPreview__btn--bottomRight'])}
                        layout={BtnLayout.Icon}
                        icon="action-fa-repeat"
                        onClick={this.handleRotate}
                    />
                )}
                {showPhotoCode && photoLoadedAndUploadedSuccesfullyAnd && (
                    <Btn
                        className={cx(styles.PhotoPreview__btn, styles['PhotoPreview__btn--bottomLeft'])}
                        layout={BtnLayout.Icon}
                        icon="action-fa-clipboard"
                        onClick={this.handleShowPhotoCode}
                    />
                )}
            </div>
        );
    }

    private handleRotate = () => {
        const { upload, onRotate } = this.props;
        const supload = upload as SuccessfulPhotoUpload;
        onRotate(supload.id, (supload.orientation + 1) % 4);
    };

    private handleCancel = () => {
        const { upload, onCancel } = this.props;
        onCancel(upload.id);
    };

    private handleShowPhotoCode = () => {
        const { uploadedPhoto } = this.props;
        if (uploadedPhoto) {
            const pictureCode = '<<pic cd="' + uploadedPhoto.code + '" size="medium" align="center" title="">>';
            prompt(WIKI_PLACE_IMAGE_IN_ARTICLE_WITH_FOLLOWING_CODE, pictureCode);
        }
    };

    private handleHiddenImageLoaded = (e) => {
        this.setState({photoLoaded: true});
    };

    private handleHiddenImageFailed = (e) => {
        this.setState({photoLoaded: false});
    };
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    const { upload } = ownProps;

    const uploadedPhoto: PhotoEntity = upload.status === PhotoUploadStatus.SUCCESS
        ? getDenormalizedEntity<PhotoEntity>(state, PhotoSchema, (upload as SuccessfulPhotoUpload).photoId)
        : null;
    const uploadedPhotoPreview: PhotoPreviewEntity = getDenormalizedEntity<PhotoPreviewEntity>(state, PhotoPreviewSchema, upload.fileName);

    return {
        uploadedPhoto,
        uploadedPhotoPreview,
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {};

export default connect(mapStateToProps, mapDispatchToProps)(PhotoPreview);
